import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { useSSERefetch } from "../hooks/useSSERefetch";
import { useLiveEvents } from "../hooks/useLiveEvents";
import StatusBadge from "../components/StatusBadge";
import PlatformBadge from "../components/PlatformBadge";
import RunStats from "../components/RunStats";
import RunGantt from "../components/RunGantt";
import SSEEventTail from "../components/SSEEventTail";
import { DiagnosticsPanel } from "../components/DiagnosticsPanel";

interface Step {
  name: string;
  status: string;
  duration_ms?: number;
  error_message?: string;
}

interface ExecutionDevice {
  id: string;
  serial: string;
  model: string;
  platform: string;
  osVersion: string;
}

interface Execution {
  id: string;
  scenarioName: string;
  featureFile: string | null;
  status: string;
  durationMs: number | null;
  errorMessage: string | null;
  attempt: number;
  steps?: Step[] | null;
  device?: ExecutionDevice | null;
  artifacts: Array<{
    id: string;
    type: string;
    gcsPath: string;
    signedUrl?: string;
    fileName?: string | null;
    fileSizeBytes?: number | null;
    stepId?: string | null;
    stepText?: string | null;
    isNew?: boolean;
  }>;
}

interface TestRun {
  id: string;
  pipelineId: string;
  jobId: string;
  mergeRequestIid: string | null;
  appName: string;
  attempt: number;
  deviceCount: number | null;
  passedCount: number;
  failedCount: number;
  totalScenarios: number;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  status: string;
  executions: Execution[];
  // CI context fields (added in Task 1)
  platform?: "android" | "ios" | "web" | null;
  ciJobName?: string | null;
  ciJobUrl?: string | null;
  ciPipelineUrl?: string | null;
  mrTitle?: string | null;
  mrUrl?: string | null;
  branch?: string | null;
  commitSha?: string | null;
  pipelineSource?: string | null;
  workerCount?: number | null;
}

function formatMs(ms: number | undefined | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export default function TestRunDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: run, loading, error, refetch } = useApi<TestRun>(`/api/runs/${id}`);
  const { events: liveEvents } = useLiveEvents("/api/events", id);
  useSSERefetch("/api/events", [
    "run:execution",        // new execution recorded → refetch to get new scenario
    "run:artifact_added",   // video/screenshot attached → refetch so it appears
    "run:completed",        // run finalized → refetch for final counts
  ], refetch);

  // Lightbox state for full-size screenshot overlay
  const [lightbox, setLightbox] = React.useState<{ src: string; label: string } | null>(null);

  // Escape key closes lightbox
  React.useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  // Local shadow of run data — allows artifact:created SSE events to splice
  // in new artifacts without a full refetch. Stays in sync with the useApi
  // data via useEffect. When a full refetch completes the shadow is updated.
  const [localRun, setLocalRun] = useState<TestRun | null>(null);
  useEffect(() => {
    setLocalRun(run);
  }, [run]);

  // Dedicated SSE subscription for artifact:created — splices the new artifact
  // into the matching execution's artifacts array in local state for real-time
  // updates without a round-trip to the API.
  useEffect(() => {
    if (!id) return;

    const es = new EventSource("/api/events", { withCredentials: true });

    const handler = (e: MessageEvent) => {
      let data: {
        run_id: string;
        execution_id: string;
        artifact_id: string;
        type: string;
        gcs_path: string;
        signed_url: string;
        step_id: string | null;
        step_text: string | null;
      };
      try {
        data = JSON.parse(e.data);
      } catch {
        return;
      }

      if (data.run_id !== id) return;

      setLocalRun((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          executions: prev.executions.map((exec) => {
            if (exec.id !== data.execution_id) return exec;
            // Avoid duplicate if artifact already present (e.g. after full refetch)
            const alreadyPresent = exec.artifacts.some((a) => a.id === data.artifact_id);
            if (alreadyPresent) return exec;
            return {
              ...exec,
              artifacts: [
                ...exec.artifacts,
                {
                  id: data.artifact_id,
                  type: data.type,
                  gcsPath: data.gcs_path,
                  signedUrl: data.signed_url,
                  stepId: data.step_id,
                  stepText: data.step_text,
                  isNew: true,
                },
              ],
            };
          }),
        };
      });

      // Clear isNew flag after 2s so the pulse animation ends
      setTimeout(() => {
        setLocalRun((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            executions: prev.executions.map((exec) => ({
              ...exec,
              artifacts: exec.artifacts.map((a) =>
                a.id === data.artifact_id ? { ...a, isNew: false } : a
              ),
            })),
          };
        });
      }, 2000);
    };

    es.addEventListener("artifact:created", handler);

    return () => {
      es.removeEventListener("artifact:created", handler);
      es.close();
    };
  }, [id]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedLiveScenario, setSelectedLiveScenario] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Per-attempt UI state for the scenario list. Long retries (820 entries)
  // need filtering/searching/collapsing to navigate.
  const [collapsedAttempts, setCollapsedAttempts] = useState<Set<number>>(new Set());
  const [attemptFilters, setAttemptFilters] = useState<Record<number, "all" | "failed" | "passed">>({});
  const [attemptSearches, setAttemptSearches] = useState<Record<number, string>>({});
  const attemptHeaderRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Drilldown filters — let investigators pivot from "this scenario" to
  // "all scenarios on this device" or "all scenarios in this feature file".
  // Cleared by clicking the chip again or selecting a scenario outside the
  // current filter.
  const [deviceFilter, setDeviceFilter] = useState<string | null>(null);
  const [featureFilter, setFeatureFilter] = useState<string | null>(null);
  const [groupByFeature, setGroupByFeature] = useState(false);

  const toggleAttemptCollapsed = useCallback((attempt: number) => {
    setCollapsedAttempts((prev) => {
      const next = new Set(prev);
      if (next.has(attempt)) next.delete(attempt);
      else next.add(attempt);
      return next;
    });
  }, []);

  const setAttemptFilter = useCallback((attempt: number, filter: "all" | "failed" | "passed") => {
    setAttemptFilters((prev) => ({ ...prev, [attempt]: filter }));
  }, []);

  const setAttemptSearch = useCallback((attempt: number, query: string) => {
    setAttemptSearches((prev) => ({ ...prev, [attempt]: query }));
  }, []);

  const scrollToAttempt = useCallback((attempt: number) => {
    const el = attemptHeaderRefs.current.get(attempt);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Prefer localRun (which may have live-spliced artifacts) over the raw API
  // data. localRun is kept in sync with run via useEffect, and augmented
  // in-place when artifact:created SSE events arrive.
  const mergedRun = localRun ?? run ?? null;
  // Sort executions by creation order (ASC) so they appear first-to-last
  const executions = useMemo(() => {
    const execs = mergedRun?.executions ?? [];
    return [...execs].reverse(); // API returns DESC, we want ASC
  }, [mergedRun]);

  // serial → model lookup, populated from completed executions. Live scenarios
  // (which only carry device_serial in the event payload) reuse this so
  // running cards show "SM-S721U (R5CY23CL)" the same way completed cards do.
  const deviceModelBySerial = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of executions) {
      const dev = e.device;
      if (dev?.serial && dev?.model) map.set(dev.serial, dev.model);
    }
    return map;
  }, [executions]);

  // Derive in-progress scenarios from live events (not yet in DB)
  const liveScenarios = useMemo(() => {
    const completedNames = new Set(executions.map(e => e.scenarioName));
    const active = new Map<string, { scenario: string; device_serial?: string; steps: Array<{ name: string; status: string; duration_ms?: number }> }>();

    for (const evt of liveEvents) {
      if (evt.event === "run:testCaseStarted") {
        const name = String(evt.scenario ?? "");
        if (name && !completedNames.has(name)) {
          active.set(name, { scenario: name, device_serial: String(evt.device_serial ?? ""), steps: [] });
        }
      }
      if (evt.event === "run:testStepFinished") {
        const name = String(evt.scenario ?? "");
        const entry = active.get(name);
        if (entry) {
          entry.steps.push({
            name: String(evt.step ?? ""),
            status: String(evt.status ?? ""),
            duration_ms: evt.duration_ms != null ? Number(evt.duration_ms) : undefined,
          });
        }
      }
      if (evt.event === "run:execution") {
        // Scenario completed — remove from active (it's now in DB)
        active.delete(String(evt.scenario_name ?? ""));
      }
    }
    return Array.from(active.values());
  }, [liveEvents, executions]);

  const selectedExec = useMemo(() => {
    if (!executions.length) return null;
    return executions.find((e) => e.id === selectedId) ?? null;
  }, [executions, selectedId]);

  // Group executions by attempt
  const attemptGroups = useMemo(() => {
    const groups = new Map<number, Execution[]>();
    for (const e of executions) {
      const attempt = e.attempt ?? 1;
      const arr = groups.get(attempt) ?? [];
      arr.push(e);
      groups.set(attempt, arr);
    }
    return Array.from(groups.entries()).sort((a, b) => a[0] - b[0]);
  }, [executions]);

  // Calculate cumulative timestamps for each step so we can seek the video
  const stepTimestamps = useMemo(() => {
    if (!selectedExec?.steps) return [];
    let cumulative = 0;
    return selectedExec.steps.map((step) => {
      const start = cumulative;
      cumulative += (step.duration_ms ?? 0);
      return { ...step, startMs: start, endMs: cumulative };
    });
  }, [selectedExec]);

  const seekToStep = useCallback((startMs: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = startMs / 1000;
      videoRef.current.play().catch(() => {});
    }
  }, []);

  const videoUrl = useMemo(() => {
    if (!selectedExec) return null;
    const vid = selectedExec.artifacts.find((a) => a.type === "video");
    return vid?.signedUrl || vid?.gcsPath || null;
  }, [selectedExec]);

  if (loading && !run) {
    return <p style={{ padding: "var(--space-xl)" }}>Loading run details...</p>;
  }

  if (error || !mergedRun) {
    return (
      <p style={{ padding: "var(--space-xl)", color: "var(--color-error)" }}>
        {error ?? "Run not found."}
      </p>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav style={{ marginBottom: "var(--space-sm)", fontSize: "var(--font-size-sm)" }}>
        <Link to="/runs" style={{ color: "var(--color-info)" }}>Test Runs</Link>
        <span style={{ margin: "0 var(--space-xs)", color: "var(--color-text-secondary)" }}>/</span>
        <span>Job {mergedRun.jobId}</span>
      </nav>

      {/* Compact header */}
      <header style={{
        background: "var(--color-surface)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-card)",
        padding: "var(--space-md) var(--space-lg)",
        marginBottom: "var(--space-md)",
      }}>
        {/* CI context row — platform badge, job name/link, pipeline link */}
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "var(--space-md)",
          alignItems: "center",
          marginBottom: "var(--space-sm)",
        }}>
          {mergedRun.platform && <PlatformBadge platform={mergedRun.platform} />}
          {mergedRun.ciJobUrl ? (
            <a
              href={mergedRun.ciJobUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <h1 style={{ fontSize: "var(--font-size-lg)", fontWeight: 700, color: "var(--color-primary)", margin: 0 }}>
                {mergedRun.ciJobName ?? `Job ${mergedRun.jobId}`}
              </h1>
            </a>
          ) : (
            <h1 style={{ fontSize: "var(--font-size-lg)", fontWeight: 700, color: "var(--color-primary)", margin: 0 }}>
              {mergedRun.ciJobName ?? `Job ${mergedRun.jobId}`}
            </h1>
          )}
          {mergedRun.ciPipelineUrl ? (
            <a
              href={mergedRun.ciPipelineUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: "var(--font-size-sm)", color: "var(--color-info)", textDecoration: "none" }}
            >
              Pipeline ↗
            </a>
          ) : mergedRun.pipelineId ? (
            <a
              href={`https://gitlab.com/gigsmart/projects/monorepo/-/pipelines/${mergedRun.pipelineId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...metaStyle, color: "var(--color-info)", textDecoration: "none" }}
              title={`Open pipeline ${mergedRun.pipelineId} in GitLab`}
            >
              pipeline #{mergedRun.pipelineId}
            </a>
          ) : null}
          <StatusBadge status={mergedRun.status} />
        </div>

        {/* Source context row — MR link or mainline branch */}
        {mergedRun.pipelineSource === "merge_request_event" && mergedRun.mrUrl ? (
          <div style={{ marginBottom: "var(--space-xs)", fontSize: "var(--font-size-sm)" }}>
            <span style={{ color: "var(--color-text-secondary)" }}>MR: </span>
            <a
              href={mergedRun.mrUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--color-info)", textDecoration: "none" }}
            >
              !{mergedRun.mergeRequestIid} {mergedRun.mrTitle}
            </a>
          </div>
        ) : mergedRun.mergeRequestIid && !mergedRun.mrUrl ? (
          // Legacy fallback: mergeRequestIid exists but no mrUrl (pre-Task-1 rows)
          <div style={{ marginBottom: "var(--space-xs)", fontSize: "var(--font-size-sm)" }}>
            <a
              href={`https://gitlab.com/gigsmart/projects/monorepo/-/merge_requests/${mergedRun.mergeRequestIid}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--color-info)", textDecoration: "none" }}
              title={`Open MR !${mergedRun.mergeRequestIid} in GitLab`}
            >
              !{mergedRun.mergeRequestIid}
            </a>
          </div>
        ) : mergedRun.branch ? (
          <div style={{ marginBottom: "var(--space-xs)", fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
            Mainline push to <code>{mergedRun.branch}</code>
          </div>
        ) : null}

        {/* Commit SHA */}
        {mergedRun.commitSha && (
          <div style={{ marginBottom: "var(--space-xs)", fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
            <code title={mergedRun.commitSha}>{mergedRun.commitSha.slice(0, 8)}</code>
          </div>
        )}

        {/* Stats row — pass/fail/devices/attempt/timing */}
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "var(--space-md)",
          alignItems: "center",
          marginTop: "var(--space-sm)",
        }}>
          <span style={metaStyle}>{mergedRun.appName}</span>
          <span style={{ ...metaStyle, color: "var(--color-success)" }}>{mergedRun.passedCount} passed</span>
          <span style={{ ...metaStyle, color: mergedRun.failedCount > 0 ? "var(--color-danger)" : "var(--color-text-secondary)" }}>
            {mergedRun.failedCount} failed
          </span>
          {mergedRun.platform === "web" && mergedRun.workerCount != null ? (
            <span style={metaStyle}>Web Workers: {mergedRun.workerCount}</span>
          ) : (mergedRun.deviceCount ?? 0) > 0 ? (
            <span style={metaStyle}>{mergedRun.deviceCount} devices</span>
          ) : null}
          {mergedRun.attempt > 1 && <span style={{ ...metaStyle, color: "var(--color-warning)" }}>Attempt #{mergedRun.attempt}</span>}
          <span style={{ ...metaStyle, marginLeft: "auto" }}>
            {mergedRun.startedAt && new Date(mergedRun.startedAt).toLocaleString()}
            {mergedRun.startedAt && mergedRun.finishedAt && ` · ${((new Date(mergedRun.finishedAt).getTime() - new Date(mergedRun.startedAt).getTime()) / 1000).toFixed(0)}s`}
            {mergedRun.startedAt && !mergedRun.finishedAt && ` · running`}
          </span>
        </div>
      </header>

      <RunStats executions={executions} totalScenarios={mergedRun.totalScenarios} />
      <RunGantt executions={executions} />
      <SSEEventTail events={liveEvents} />

      {/* 3-Column Layout */}
      <div style={{ display: "grid", gridTemplateColumns: "280px 1fr 360px", gap: "var(--space-md)", minHeight: 500 }}>

        {/* Column 1: Scenario List */}
        <div style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-md)",
          boxShadow: "var(--shadow-card)",
          overflow: "auto",
          maxHeight: "calc(100vh - 200px)",
        }}>
          {/* In-progress scenarios from live events */}
          {liveScenarios.length > 0 && (
            <div>
              <div style={{
                padding: "var(--space-xs) var(--space-md)",
                background: "var(--color-status-busy-bg)",
                borderBottom: "var(--border-default)",
                fontSize: "var(--font-size-xs)",
                fontWeight: 600,
                color: "var(--color-warning)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                display: "flex",
                alignItems: "center",
                gap: "var(--space-xs)",
              }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--color-success)", display: "inline-block" }} />
                Running ({liveScenarios.length})
              </div>
              {liveScenarios.map((ls) => (
                <div
                  key={ls.scenario}
                  onClick={() => {
                    setSelectedId(null);
                    setSelectedLiveScenario(selectedLiveScenario === ls.scenario ? null : ls.scenario);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-xs)",
                    padding: "var(--space-sm) var(--space-md)",
                    cursor: "pointer",
                    borderBottom: "1px solid var(--color-foreground)",
                    borderLeft: selectedLiveScenario === ls.scenario ? "3px solid var(--color-info)" : "3px solid var(--color-warning)",
                    background: selectedLiveScenario === ls.scenario ? "var(--color-foreground)" : "transparent",
                  }}
                >
                  <span style={{ color: "var(--color-warning)", fontSize: "var(--font-size-sm)", width: 16, textAlign: "center", flexShrink: 0 }}>
                    {"\u25CF"}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "var(--font-size-sm)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {ls.scenario}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>
                      {ls.device_serial
                        ? `${deviceModelBySerial.get(ls.device_serial) ?? "?"} (${ls.device_serial.slice(0, 8)})`
                        : ""}
                      {" · step "}{ls.steps.length}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Drilldown filter bar — surfaces active device / feature filters
              and a toggle for nested feature-file grouping. Investigators
              click "Filter by this device" / "Filter by this feature" in
              the scenario detail to land here. */}
          {(deviceFilter || featureFilter || groupByFeature) && (
            <div style={{
              padding: "var(--space-xs) var(--space-md)",
              background: "var(--color-status-claimed-bg)",
              borderBottom: "var(--border-default)",
              fontSize: "var(--font-size-xs)",
              display: "flex",
              gap: "var(--space-xs)",
              alignItems: "center",
              flexWrap: "wrap",
            }}>
              {deviceFilter && (
                <button
                  onClick={() => setDeviceFilter(null)}
                  title="Clear device filter"
                  style={chipStyle}
                >
                  device: {deviceFilter.slice(0, 8)} ×
                </button>
              )}
              {featureFilter && (
                <button
                  onClick={() => setFeatureFilter(null)}
                  title="Clear feature filter"
                  style={chipStyle}
                >
                  feature: {featureFilter.split("/").pop()} ×
                </button>
              )}
              {groupByFeature && (
                <button
                  onClick={() => setGroupByFeature(false)}
                  title="Stop grouping by feature file"
                  style={chipStyle}
                >
                  grouped by feature ×
                </button>
              )}
            </div>
          )}

          {/* Sticky jump bar — only renders when there are multiple attempts. */}
          {attemptGroups.length > 1 && (
            <div style={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              padding: "var(--space-xs) var(--space-md)",
              background: "var(--color-surface)",
              borderBottom: "var(--border-default)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
              display: "flex",
              gap: "var(--space-xs)",
              fontSize: "var(--font-size-xs)",
              alignItems: "center",
              flexWrap: "wrap",
            }}>
              <span style={{ color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600 }}>Jump:</span>
              {attemptGroups.map(([attempt, execs]) => (
                <button
                  key={attempt}
                  onClick={() => scrollToAttempt(attempt)}
                  style={{
                    padding: "2px 8px",
                    fontSize: "var(--font-size-xs)",
                    background: attempt === 1 ? "var(--color-foreground)" : "var(--color-status-busy-bg)",
                    color: attempt > 1 ? "var(--color-warning)" : "var(--color-text-primary)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  {attempt === 1 ? `Attempt 1` : `Retry ${attempt - 1}`} ({execs.length})
                </button>
              ))}
            </div>
          )}

          {attemptGroups.map(([attempt, attemptExecs]) => {
            const passed = attemptExecs.filter(e => e.status === "passed").length;
            const failed = attemptExecs.filter(e => e.status === "failed").length;
            const collapsed = collapsedAttempts.has(attempt);
            const filter = attemptFilters[attempt] ?? "all";
            const search = (attemptSearches[attempt] ?? "").trim().toLowerCase();

            const filteredExecs = attemptExecs.filter((e) => {
              if (filter === "passed" && e.status !== "passed") return false;
              if (filter === "failed" && e.status !== "failed") return false;
              if (search && !e.scenarioName.toLowerCase().includes(search)) return false;
              if (deviceFilter && e.device?.serial !== deviceFilter) return false;
              if (featureFilter && (e.featureFile ?? "") !== featureFilter) return false;
              return true;
            });

            // Optional feature-file grouping inside the attempt — toggled via
            // the "group by feature" chip in the scenario detail. Preserves
            // execution order within each group.
            const featureGroups: Array<[string, Execution[]]> = (() => {
              if (!groupByFeature) return [["", filteredExecs]];
              const map = new Map<string, Execution[]>();
              for (const e of filteredExecs) {
                const key = e.featureFile ?? "(no feature file)";
                const arr = map.get(key) ?? [];
                arr.push(e);
                map.set(key, arr);
              }
              return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
            })();

            return (
              <div key={attempt}>
                {/* Attempt header — clickable to collapse, sticky below the jump bar. */}
                <div
                  ref={(el) => {
                    if (el) attemptHeaderRefs.current.set(attempt, el);
                    else attemptHeaderRefs.current.delete(attempt);
                  }}
                  onClick={() => toggleAttemptCollapsed(attempt)}
                  style={{
                    padding: "var(--space-xs) var(--space-md)",
                    background: attempt === 1 ? "var(--color-foreground)" : "var(--color-status-busy-bg)",
                    borderBottom: "var(--border-default)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: 600,
                    color: attempt > 1 ? "var(--color-warning)" : "var(--color-text-secondary)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    position: "sticky",
                    top: attemptGroups.length > 1 ? 32 : 0,
                    zIndex: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <span>
                    <span style={{ display: "inline-block", width: 12, marginRight: 4 }}>
                      {collapsed ? "▸" : "▾"}
                    </span>
                    {attempt === 1 ? `Attempt 1` : `Retry ${attempt - 1}`} ({attemptExecs.length})
                  </span>
                  <span>
                    <span style={{ color: "var(--color-success)" }}>{passed} {"\u2713"}</span>
                    {failed > 0 && <span style={{ color: "var(--color-danger)", marginLeft: 6 }}>{failed} {"\u2717"}</span>}
                  </span>
                </div>

                {/* Filter + search toolbar \u2014 hidden when attempt is collapsed. */}
                {!collapsed && (
                  <div
                    style={{
                      padding: "var(--space-xs) var(--space-md)",
                      background: "var(--color-background)",
                      borderBottom: "1px solid var(--color-foreground)",
                      display: "flex",
                      gap: "var(--space-xs)",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {(["all", "failed", "passed"] as const).map((f) => {
                      const active = filter === f;
                      return (
                        <button
                          key={f}
                          onClick={() => setAttemptFilter(attempt, f)}
                          style={{
                            padding: "2px 8px",
                            fontSize: "var(--font-size-xs)",
                            background: active ? "var(--color-info)" : "transparent",
                            color: active ? "var(--color-background)" : "var(--color-text-secondary)",
                            border: `1px solid ${active ? "var(--color-info)" : "var(--color-border)"}`,
                            borderRadius: 4,
                            cursor: "pointer",
                            textTransform: "capitalize",
                          }}
                        >
                          {f}
                        </button>
                      );
                    })}
                    <input
                      type="text"
                      value={attemptSearches[attempt] ?? ""}
                      onChange={(e) => setAttemptSearch(attempt, e.target.value)}
                      placeholder="Search scenarios\u2026"
                      style={{
                        flex: 1,
                        minWidth: 120,
                        padding: "2px 8px",
                        fontSize: "var(--font-size-xs)",
                        background: "var(--color-surface)",
                        color: "var(--color-text-primary)",
                        border: "1px solid var(--color-border)",
                        borderRadius: 4,
                      }}
                    />
                    {(filter !== "all" || search) && (
                      <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                        showing {filteredExecs.length} / {attemptExecs.length}
                      </span>
                    )}
                  </div>
                )}

                {!collapsed && featureGroups.map(([featureKey, execs]) => (
                  <div key={featureKey || "_all"}>
                    {groupByFeature && featureKey && (
                      <div
                        style={{
                          padding: "var(--space-xs) var(--space-md)",
                          background: "var(--color-background)",
                          borderBottom: "1px solid var(--color-foreground)",
                          fontSize: 10,
                          fontFamily: "var(--font-mono)",
                          color: "var(--color-text-secondary)",
                          textTransform: "none",
                          letterSpacing: 0,
                        }}
                        title={featureKey}
                      >
                        {featureKey} ({execs.length})
                      </div>
                    )}
                    {execs.map((e) => (
                      <div
                        key={e.id}
                        onClick={() => {
                          setSelectedLiveScenario(null);
                          setSelectedId(selectedId === e.id ? null : e.id);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "var(--space-xs)",
                          padding: "var(--space-sm) var(--space-md)",
                          cursor: "pointer",
                          borderBottom: "1px solid var(--color-foreground)",
                          background: selectedId === e.id ? "var(--color-foreground)" : "transparent",
                          borderLeft: selectedId === e.id ? "3px solid var(--color-info)" : "3px solid transparent",
                        }}
                      >
                        <span style={{
                          color: e.status === "passed" ? "var(--color-success)"
                            : e.status === "failed" ? "var(--color-danger)"
                            : "var(--color-warning)",
                          fontSize: "var(--font-size-sm)",
                          flexShrink: 0,
                          width: 16,
                          textAlign: "center",
                        }}>
                          {e.status === "passed" ? "\u2713" : e.status === "failed" ? "\u2717" : "\u25CF"}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "var(--font-size-sm)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {e.scenarioName}
                          </div>
                          {e.device && (
                            <div style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>
                              {e.device.model} ({e.device.serial.slice(0, 8)})
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: 11, color: "var(--color-text-secondary)", flexShrink: 0 }}>
                          {formatMs(e.durationMs)}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Column 2: Steps + Details */}
        <div style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-md)",
          boxShadow: "var(--shadow-card)",
          overflow: "auto",
          maxHeight: "calc(100vh - 200px)",
        }}>
          {selectedLiveScenario ? (() => {
            const ls = liveScenarios.find(s => s.scenario === selectedLiveScenario);
            if (!ls) return <div style={{ padding: "var(--space-xl)", textAlign: "center", color: "var(--color-text-secondary)" }}>Scenario completed — select from list below</div>;
            return (
              <div style={{ padding: "var(--space-md)" }}>
                <div style={{ marginBottom: "var(--space-md)" }}>
                  <div style={{ fontSize: "var(--font-size-md)", fontWeight: 600, marginBottom: 4 }}>
                    {ls.scenario}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)" }}>
                    <StatusBadge status="running" />
                    {ls.device_serial && (
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--color-text-secondary)" }}>
                        {ls.device_serial}
                      </span>
                    )}
                    <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
                      Step {ls.steps.length}
                    </span>
                  </div>
                </div>
                <div style={{
                  fontSize: "var(--font-size-xs)",
                  fontWeight: 600,
                  color: "var(--color-text-secondary)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "var(--space-xs)",
                }}>
                  Steps ({ls.steps.length}) · live
                </div>
                {ls.steps.map((step, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "var(--space-xs)",
                      padding: "4px 0",
                      fontSize: "var(--font-size-sm)",
                      borderBottom: i < ls.steps.length - 1 ? "1px solid var(--color-foreground)" : "none",
                    }}
                  >
                    <span style={{
                      color: step.status === "passed" ? "var(--color-success)"
                        : step.status === "failed" ? "var(--color-danger)"
                        : "var(--color-neutral)",
                      flexShrink: 0,
                      width: 16,
                      textAlign: "center",
                    }}>
                      {step.status === "passed" ? "\u2714" : step.status === "failed" ? "\u2718" : "\u25CB"}
                    </span>
                    <span style={{ flex: 1 }}>{step.name}</span>
                    <span style={{ color: "var(--color-text-secondary)", fontSize: 11, flexShrink: 0 }}>
                      {formatMs(step.duration_ms)}
                    </span>
                  </div>
                ))}
              </div>
            );
          })() : selectedExec ? (
            <div style={{ padding: "var(--space-md)" }}>
              {/* Scenario header */}
              <div style={{ marginBottom: "var(--space-md)" }}>
                <div style={{ fontSize: "var(--font-size-md)", fontWeight: 600, marginBottom: 4 }}>
                  {selectedExec.scenarioName}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)", flexWrap: "wrap" }}>
                  <StatusBadge status={selectedExec.status} />
                  <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
                    {formatMs(selectedExec.durationMs)}
                  </span>
                  {selectedExec.featureFile && (
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--color-text-secondary)" }}>
                      {selectedExec.featureFile}
                    </span>
                  )}
                </div>
                {selectedExec.device && (
                  <div style={{
                    marginTop: "var(--space-sm)",
                    padding: "var(--space-xs) var(--space-sm)",
                    background: "var(--color-foreground)",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "var(--font-size-xs)",
                    display: "inline-flex",
                    gap: "var(--space-md)",
                  }}>
                    <span><b>{selectedExec.device.model}</b></span>
                    <span style={{ fontFamily: "var(--font-mono)" }}>{selectedExec.device.serial}</span>
                    <span>Android {selectedExec.device.osVersion}</span>
                  </div>
                )}
                {/* Drilldown actions — pivot scenario-list to per-device or
                    per-feature views. Lets investigators jump from "this
                    scenario failed" to "show me everything that ran on this
                    device / in this feature file". */}
                <div style={{
                  marginTop: "var(--space-sm)",
                  display: "flex",
                  gap: "var(--space-xs)",
                  flexWrap: "wrap",
                }}>
                  {selectedExec.device && (
                    <button
                      type="button"
                      onClick={() => setDeviceFilter(
                        deviceFilter === selectedExec.device!.serial ? null : selectedExec.device!.serial
                      )}
                      style={{
                        ...filterActionStyle,
                        ...(deviceFilter === selectedExec.device.serial ? {
                          background: "var(--color-info)",
                          color: "var(--color-surface)",
                        } : {}),
                      }}
                      title="Filter scenario list to this device only"
                    >
                      {deviceFilter === selectedExec.device.serial ? "✓ This device only" : "Filter by this device"}
                    </button>
                  )}
                  {selectedExec.featureFile && (
                    <button
                      type="button"
                      onClick={() => setFeatureFilter(
                        featureFilter === selectedExec.featureFile ? null : selectedExec.featureFile
                      )}
                      style={{
                        ...filterActionStyle,
                        ...(featureFilter === selectedExec.featureFile ? {
                          background: "var(--color-info)",
                          color: "var(--color-surface)",
                        } : {}),
                      }}
                      title="Filter scenario list to this feature file only"
                    >
                      {featureFilter === selectedExec.featureFile ? "✓ This feature only" : "Filter by this feature"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setGroupByFeature((v) => !v)}
                    style={{
                      ...filterActionStyle,
                      ...(groupByFeature ? {
                        background: "var(--color-info)",
                        color: "var(--color-surface)",
                      } : {}),
                    }}
                    title="Group the scenario list by feature file"
                  >
                    {groupByFeature ? "✓ Grouped by feature" : "Group by feature"}
                  </button>
                </div>
              </div>

              {/* Steps — clickable to seek video; screenshots grouped inline per step */}
              {stepTimestamps.length > 0 && (
                <div>
                  <div style={{
                    fontSize: "var(--font-size-xs)",
                    fontWeight: 600,
                    color: "var(--color-text-secondary)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: "var(--space-xs)",
                  }}>
                    Steps ({stepTimestamps.length})
                    {videoUrl && <span style={{ fontWeight: 400, textTransform: "none" }}> · click to seek video</span>}
                  </div>
                  {stepTimestamps.map((step, i) => {
                    // Match screenshots to this step by Gherkin text.
                    // artifact.stepText is the Gherkin step string; step.name is the same string.
                    const stepScreenshots = (selectedExec?.artifacts ?? []).filter(
                      (a) => a.type === "screenshot" && a.stepText === step.name
                    );
                    return (
                      <div key={i} style={{ borderBottom: i < stepTimestamps.length - 1 ? "1px solid var(--color-foreground)" : "none" }}>
                        {/* Step row — click to seek video */}
                        <div
                          onClick={() => videoUrl && seekToStep(step.startMs)}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "var(--space-xs)",
                            padding: "4px 0",
                            fontSize: "var(--font-size-sm)",
                            cursor: videoUrl ? "pointer" : "default",
                          }}
                          title={videoUrl ? `Seek to ${(step.startMs / 1000).toFixed(1)}s` : undefined}
                        >
                          <span style={{
                            color: step.status === "passed" ? "var(--color-success)"
                              : step.status === "failed" ? "var(--color-danger)"
                              : "var(--color-neutral)",
                            flexShrink: 0,
                            width: 16,
                            textAlign: "center",
                          }}>
                            {step.status === "passed" ? "\u2714" : step.status === "failed" ? "\u2718" : "\u25CB"}
                          </span>
                          <span style={{ flex: 1 }}>{step.name}</span>
                          <span style={{ color: "var(--color-text-secondary)", fontSize: 11, flexShrink: 0 }}>
                            {formatMs(step.duration_ms)}
                          </span>
                        </div>
                        {/* Per-step inline screenshot thumbnails — click to open lightbox */}
                        {stepScreenshots.length > 0 && (
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", paddingBottom: 6, paddingLeft: 20 }}>
                            {stepScreenshots.map((a) => (
                              <button
                                key={a.id}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation(); // don't trigger video seek
                                  setLightbox({ src: a.signedUrl ?? a.gcsPath, label: a.stepText ?? step.name });
                                }}
                                style={{
                                  padding: 0,
                                  border: "1px solid var(--color-divider)",
                                  borderRadius: 4,
                                  background: "var(--color-surface)",
                                  cursor: "pointer",
                                  animation: a.isNew ? "artifactPulseNew 2s ease-out forwards" : undefined,
                                  display: "block",
                                }}
                                title={`View screenshot: ${a.stepText ?? step.name}`}
                              >
                                <img
                                  src={a.signedUrl || a.gcsPath}
                                  alt={a.stepText ? `Step: ${a.stepText}` : "Screenshot"}
                                  style={{ display: "block", width: 80, height: 60, objectFit: "cover", borderRadius: 3 }}
                                />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Error message */}
              {selectedExec.errorMessage && (
                <div style={{
                  marginTop: "var(--space-md)",
                  padding: "var(--space-sm)",
                  background: "var(--color-status-offline-bg)",
                  borderRadius: "var(--radius-sm)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--color-error)",
                  whiteSpace: "pre-wrap",
                  overflowX: "auto",
                  maxHeight: 200,
                }}>
                  {selectedExec.errorMessage}
                </div>
              )}

              {/* Diagnostics — logcat, device_state, tombstone. Failure
                  scenarios surface all three; passing scenarios only have
                  the logcat tail. */}
              <DiagnosticsPanel artifacts={selectedExec.artifacts} />
            </div>
          ) : liveEvents.length > 0 ? (
            <div style={{ padding: "var(--space-md)" }}>
              <div style={{
                fontSize: "var(--font-size-xs)",
                fontWeight: 600,
                color: "var(--color-info)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "var(--space-sm)",
                display: "flex",
                alignItems: "center",
                gap: "var(--space-xs)",
              }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--color-success)", display: "inline-block", animation: "pulse 1.5s infinite" }} />
                Live Activity
              </div>
              <div style={{ maxHeight: 400, overflow: "auto" }}>
                {liveEvents.slice(-30).reverse().map((evt, i) => (
                  <div key={i} style={{
                    fontSize: "var(--font-size-sm)",
                    padding: "2px 0",
                    borderBottom: "1px solid var(--color-foreground)",
                    display: "flex",
                    gap: "var(--space-xs)",
                    alignItems: "center",
                  }}>
                    {evt.event === "run:testCaseStarted" && (
                      <>
                        <span style={{ color: "var(--color-info)" }}>{"\u25B6"}</span>
                        <span>{String(evt.scenario ?? "")}</span>
                      </>
                    )}
                    {evt.event === "run:testStepFinished" && (
                      <>
                        <span style={{ color: evt.status === "passed" ? "var(--color-success)" : evt.status === "failed" ? "var(--color-danger)" : "var(--color-neutral)" }}>
                          {evt.status === "passed" ? "\u2714" : evt.status === "failed" ? "\u2718" : "\u25CB"}
                        </span>
                        <span style={{ color: "var(--color-text-secondary)" }}>{String(evt.step ?? "")}</span>
                        {evt.duration_ms != null && (
                          <span style={{ fontSize: 10, color: "var(--color-text-secondary)", marginLeft: "auto" }}>
                            {Number(evt.duration_ms) < 1000 ? `${evt.duration_ms}ms` : `${(Number(evt.duration_ms) / 1000).toFixed(1)}s`}
                          </span>
                        )}
                      </>
                    )}
                    {evt.event === "run:execution" && (
                      <>
                        <span style={{ color: evt.status === "passed" ? "var(--color-success)" : "var(--color-danger)" }}>
                          {evt.status === "passed" ? "\u2713" : "\u2717"}
                        </span>
                        <span style={{ fontWeight: 500 }}>{String(evt.scenario_name ?? "")}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ padding: "var(--space-xl)", textAlign: "center", color: "var(--color-text-secondary)" }}>
              Select a scenario to view steps
            </div>
          )}
        </div>

        {/* Column 3: Video + Artifacts */}
        <div style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-md)",
          boxShadow: "var(--shadow-card)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}>
          {/* Keyframes for the live artifact arrival pulse */}
          <style>{`
            @keyframes artifactPulseNew {
              0%   { box-shadow: 0 0 0 4px rgba(62, 175, 224, 0.6); }
              100% { box-shadow: 0 0 0 0 transparent; }
            }
          `}</style>
          {selectedExec && videoUrl ? (
            <>
              <video
                ref={videoRef}
                key={videoUrl}
                controls
                style={{ width: "100%", background: "#000", flexShrink: 0 }}
              >
                <source src={videoUrl} />
              </video>
              {/* Artifact tray — orphaned screenshots (no stepText) + fallback.
                  Screenshots matched to steps are rendered inline in Column 2. */}
              {(() => {
                const orphaned = selectedExec.artifacts.filter(
                  (a) => a.type === "screenshot" && !a.stepText
                );
                const allScreenshots = selectedExec.artifacts.filter((a) => a.type === "screenshot");
                if (orphaned.length === 0 && allScreenshots.length > 0) {
                  return (
                    <div style={{ padding: "var(--space-sm)", borderTop: "var(--border-default)" }}>
                      <div style={{ fontSize: 10, color: "var(--color-text-secondary)", textTransform: "uppercase", marginBottom: 4 }}>
                        Screenshots — grouped by step above
                      </div>
                    </div>
                  );
                }
                if (orphaned.length === 0) return null;
                return (
                  <div style={{ padding: "var(--space-sm)", borderTop: "var(--border-default)" }}>
                    <div style={{ fontSize: 10, color: "var(--color-text-secondary)", textTransform: "uppercase", marginBottom: 4 }}>
                      Screenshots
                    </div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {orphaned.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => setLightbox({ src: a.signedUrl ?? a.gcsPath, label: a.stepText ?? "" })}
                          style={{
                            padding: 0,
                            border: "1px solid var(--color-divider)",
                            borderRadius: 4,
                            background: "var(--color-surface)",
                            cursor: "pointer",
                            animation: a.isNew ? "artifactPulseNew 2s ease-out forwards" : undefined,
                            display: "block",
                          }}
                        >
                          <img
                            src={a.signedUrl || a.gcsPath}
                            alt={a.stepText ? `Step: ${a.stepText}` : "Screenshot"}
                            style={{ height: 60, borderRadius: 4, display: "block" }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </>
          ) : selectedExec ? (
            <>
              {/* No video — show orphaned screenshots (no stepText) as fallback.
                  Step-matched screenshots are grouped inline in Column 2. */}
              {(() => {
                const orphaned = selectedExec.artifacts.filter(
                  (a) => a.type === "screenshot" && !a.stepText
                );
                const allScreenshots = selectedExec.artifacts.filter((a) => a.type === "screenshot");
                if (allScreenshots.length > 0 && orphaned.length === 0) {
                  return (
                    <div style={{ padding: "var(--space-sm)" }}>
                      <div style={{ fontSize: 10, color: "var(--color-text-secondary)", textTransform: "uppercase", marginBottom: 4 }}>
                        Screenshots — grouped by step above
                      </div>
                    </div>
                  );
                }
                if (orphaned.length > 0) {
                  return (
                    <div style={{ padding: "var(--space-sm)" }}>
                      <div style={{ fontSize: 10, color: "var(--color-text-secondary)", textTransform: "uppercase", marginBottom: 4 }}>
                        Screenshots
                      </div>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {orphaned.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => setLightbox({ src: a.signedUrl ?? a.gcsPath, label: a.stepText ?? "" })}
                            style={{
                              padding: 0,
                              border: "1px solid var(--color-divider)",
                              borderRadius: 4,
                              background: "var(--color-surface)",
                              cursor: "pointer",
                              animation: a.isNew ? "artifactPulseNew 2s ease-out forwards" : undefined,
                              display: "block",
                            }}
                          >
                            <img
                              src={a.signedUrl || a.gcsPath}
                              alt={a.stepText ? `Step: ${a.stepText}` : "Screenshot"}
                              style={{ height: 60, borderRadius: 4, display: "block" }}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                }
                return (
                  <div style={{ padding: "var(--space-xl)", textAlign: "center", color: "var(--color-text-secondary)", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    No video available
                  </div>
                );
              })()}
            </>
          ) : (
            <div style={{ padding: "var(--space-xl)", textAlign: "center", color: "var(--color-text-secondary)", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              Select a scenario to view video
            </div>
          )}
        </div>
      </div>

      {/* Lightbox overlay — full-size screenshot viewer */}
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setLightbox(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.85)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            gap: "var(--space-md)",
            padding: "var(--space-xl)",
          }}
        >
          <img
            src={lightbox.src}
            alt={lightbox.label}
            style={{ maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain" }}
            onClick={(e) => e.stopPropagation()}
          />
          {lightbox.label && (
            <div style={{ color: "#fff", fontFamily: "var(--font-mono)", fontSize: "var(--font-size-sm)", textAlign: "center", maxWidth: "80vw" }}>
              {lightbox.label}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const metaStyle: React.CSSProperties = {
  fontSize: "var(--font-size-sm)",
  color: "var(--color-text-secondary)",
};

const chipStyle: React.CSSProperties = {
  padding: "2px 8px",
  fontSize: "var(--font-size-xs)",
  background: "var(--color-status-claimed-bg)",
  color: "var(--color-status-claimed-text)",
  border: "1px solid var(--color-status-claimed-text)",
  borderRadius: "var(--radius-pill)",
  cursor: "pointer",
};

const filterActionStyle: React.CSSProperties = {
  padding: "2px 8px",
  fontSize: "var(--font-size-xs)",
  background: "transparent",
  color: "var(--color-info)",
  border: "1px solid var(--color-info)",
  borderRadius: "var(--radius-sm)",
  cursor: "pointer",
};
